/**
 * Whether this viewer has collapsed the area rail.
 *
 * A per-viewer convenience, not shell state: it belongs to the person and the
 * browser they are sitting at, so it lives in `localStorage` and never reaches
 * the server. Nothing downstream depends on it being readable — a private
 * window, cleared site data or a browser set to block storage all fall back to
 * "expanded", which is the safe default because it is the state in which every
 * destination is visible.
 *
 * Every access is guarded: `localStorage` does not merely return empty in those
 * contexts, it THROWS on the accessor itself.
 */
const KEY = "ks.portal.railCollapsed";

/**
 * The stored choice, or `null` when the viewer has never made one.
 *
 * The tri-state matters: "never chosen" and "chose expanded" must not read the
 * same, or the narrow-viewport default below would override an explicit
 * decision to keep the rail open.
 */
export function readRailCollapsed(): boolean | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw === null ? null : raw === "1";
  } catch {
    return null;
  }
}

/**
 * Below this the rail and a usable docked frame no longer both fit.
 * 1024px is the standard tablet-landscape / half-width-laptop line.
 */
export const NARROW_VIEWPORT = "(max-width: 1024px)";

export function viewportIsNarrow(): boolean {
  try {
    return window.matchMedia(NARROW_VIEWPORT).matches;
  } catch {
    return false;
  }
}

/**
 * What the rail should do on first paint.
 *
 * An explicit choice always wins. With no choice on record a narrow viewport
 * starts collapsed, so the first session on a small screen is usable rather
 * than something the person has to fix before they can work.
 */
export function initialRailCollapsed(): boolean {
  const stored = readRailCollapsed();
  return stored ?? viewportIsNarrow();
}

export function writeRailCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(KEY, collapsed ? "1" : "0");
  } catch {
    // Not being able to remember the preference is not a reason to fail the
    // toggle — the rail still collapses for this session.
  }
}

/**
 * True when a keyboard event is the rail-toggle chord.
 *
 * Cmd+\ on a Mac, Ctrl+\ elsewhere — deliberately the same chord Notion uses,
 * because this is the control people already have the muscle memory for.
 *
 * Exported so the behaviour is testable without a DOM: the effect that listens
 * is a one-liner around this predicate.
 */
export function isRailToggleChord(e: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}): boolean {
  if (e.key !== "\\") return false;
  if (e.altKey) return false;
  return e.metaKey || e.ctrlKey;
}
